import { db, functions } from "@/firebase";
import { collection, doc, getDoc, getDocs, query, where, updateDoc, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import type { ShareLink, ShareScope, ShareTokenValidationResult } from "@/types/share";
import { logEvent } from "./analytics";

// ─────────────────────────────────────────────────────────────────────────────
// NEW: Invite-based evaluator share system
// ─────────────────────────────────────────────────────────────────────────────

export interface EvaluatorInvite {
  id: string;
  caseId: string;
  inviterUid: string;
  inviterName?: string;
  inviteeEmail: string;
  role: 'evaluator';
  scopes: ShareScope[];
  expiryDays: number;
  status: 'pending' | 'accepted' | 'declined' | 'revoked';
  expiresAt: Timestamp;
  createdAt: Timestamp;
}

/**
 * Send an evaluator invitation via email.
 * The Cloud Function creates the invitation doc, sends the email immediately
 * via SendGrid, and creates an in-app notification if the recipient has an account.
 */
export async function createEvaluatorInvite(
  caseId: string,
  scopes: ShareScope[],
  expiryDays: 7 | 30 | 90,
  recipientEmail: string,
): Promise<{ inviteId: string }> {
  const fn = httpsCallable<
    { caseId: string; scopes: ShareScope[]; expiryDays: number; recipientEmail: string },
    { inviteId: string }
  >(functions, 'createEvaluatorInvite');

  const result = await fn({ caseId, scopes, expiryDays, recipientEmail });
  if (!result.data?.inviteId) {
    throw new Error('Failed to create evaluator invite');
  }
  return result.data;
}

/**
 * Accept an evaluator invitation (server-side via Cloud Function).
 * Writes the VIEW_ONLY member doc with scope-based permissions.
 */
export async function acceptEvaluatorInvite(
  inviteId: string,
): Promise<{ caseId: string; redirectPath: string; alreadyMember: boolean }> {
  const fn = httpsCallable<
    { inviteId: string },
    { caseId: string; redirectPath: string; alreadyMember: boolean }
  >(functions, 'acceptEvaluatorInvite');

  const result = await fn({ inviteId });
  if (!result.data) {
    throw new Error('Failed to accept evaluator invite');
  }
  return result.data;
}

/**
 * List evaluator invitations for a case.
 * uid must be the caller's uid — required by Firestore security rules
 * (rules check inviterUid == request.auth.uid for list queries).
 */
export async function listEvaluatorInvites(caseId: string, uid: string): Promise<EvaluatorInvite[]> {
  const q = query(
    collection(db, 'invitations'),
    where('caseId', '==', caseId),
    where('role', '==', 'evaluator'),
    where('inviterUid', '==', uid),
  );
  const snap = await getDocs(q);

  const invites: EvaluatorInvite[] = snap.docs.map(d => ({
    id: d.id,
    ...(d.data() as Omit<EvaluatorInvite, 'id'>),
  }));

  // Sort newest first
  invites.sort((a, b) => {
    const aMs = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : 0;
    const bMs = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : 0;
    return bMs - aMs;
  });

  return invites;
}

/**
 * Revoke a pending evaluator invitation (case owner action).
 */
export async function revokeEvaluatorInvite(inviteId: string): Promise<void> {
  const ref = doc(db, 'invitations', inviteId);
  await updateDoc(ref, { status: 'revoked', revokedAt: new Date() });
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy share-link utilities (kept for backward compatibility)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a share link for evaluator access.
 * @deprecated Use createEvaluatorInvite() instead.
 */
export async function createShareLink(
  caseId: string,
  scopes: ShareScope[],
  expiryDays: 7 | 30 | 90,
  recipientEmail?: string,
): Promise<{ linkId: string; token: string; url: string }> {
  // Call Cloud Function to create secure token.
  // Pass baseUrl so the function can build the full share URL server-side
  // and persist it atomically — avoiding a separate client-side Firestore write
  // that was previously blocked by security rules.
  const createToken = httpsCallable<
    { caseId: string; scopes: ShareScope[]; expiryDays: number; recipientEmail?: string; baseUrl?: string },
    { linkId: string; token: string; tokenHash: string; url: string }
  >(functions, 'createShareToken');

  const baseUrl = window.location.origin;
  const result = await createToken({ caseId, scopes, expiryDays, baseUrl, ...(recipientEmail ? { recipientEmail } : {}) });
  
  if (!result.data) {
    throw new Error("Failed to create share token");
  }

  const { linkId, token, url } = result.data;

  return { linkId, token, url };
}

/**
 * List share links for a case.
 */
export async function listShareLinks(caseId: string): Promise<ShareLink[]> {
  const linksRef = collection(db, 'shares');
  const q = query(linksRef, where('caseId', '==', caseId));
  
  const snapshot = await getDocs(q);
  const links: ShareLink[] = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    links.push({
      id: docSnap.id,
      caseId: data.caseId,
      scopes: data.scopes || [],
      role: data.role || 'evaluator',
      expiresAt: data.expiresAt,
      createdBy: data.createdBy,
      createdAt: data.createdAt,
      isDisabled: data.isDisabled || false,
      tokenHash: data.tokenHash,
      url: data.url,
    });
  }

  // Sort by creation date (newest first)
  links.sort((a, b) => {
    const aTime = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : 0;
    const bTime = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : 0;
    return bTime - aTime;
  });

  return links;
}

/**
 * Disable a share link.
 */
export async function disableShareLink(linkId: string): Promise<void> {
  const linkRef = doc(db, 'shares', linkId);
  await updateDoc(linkRef, {
    isDisabled: true,
  });
}

/**
 * Check if a share link is valid.
 */
export async function validateShareLink(linkId: string, _token: string): Promise<ShareLink | null> {
  const linkRef = doc(db, 'shares', linkId);
  const linkSnap = await getDoc(linkRef);

  if (!linkSnap.exists()) {
    return null;
  }

  const data = linkSnap.data();
  const link: ShareLink = {
    id: linkSnap.id,
    caseId: data.caseId,
    scopes: data.scopes || [],
    role: data.role || 'evaluator',
    expiresAt: data.expiresAt,
    createdBy: data.createdBy,
    createdAt: data.createdAt,
    isDisabled: data.isDisabled || false,
    tokenHash: data.tokenHash,
  };

  // Check if disabled
  if (link.isDisabled) {
    return null;
  }

  // Check if expired
  const now = new Date();
  const expiryDate = link.expiresAt instanceof Timestamp ? link.expiresAt.toDate() : new Date(link.expiresAt);
  if (now > expiryDate) {
    return null;
  }

  // Validate token hash (would need crypto check in production)
  // For MVP, we trust the token parameter
  
  return link;
}

/**
 * Get scope display names.
 */
export function getScopeLabel(scope: ShareScope): string {
  const labels: Record<ShareScope, string> = {
    journal: 'Journal Entries',
    timeline: 'Timeline',
    exhibits: 'Exhibits',
    exports: 'Exports',
  };
  return labels[scope] || scope;
}

/**
 * Check if link has expired.
 */
export function isLinkExpired(link: ShareLink): boolean {
  const now = new Date();
  const expiryDate = link.expiresAt instanceof Timestamp ? link.expiresAt.toDate() : new Date(link.expiresAt);
  return now > expiryDate;
}

/**
 * Get days until expiry.
 */
export function getDaysUntilExpiry(link: ShareLink): number {
  const now = new Date();
  const expiryDate = link.expiresAt instanceof Timestamp ? link.expiresAt.toDate() : new Date(link.expiresAt);
  const diff = expiryDate.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ========================================
// Share Token Validation (Client-Side, Direct Firestore)
// ========================================

/**
 * Client-side SHA-256 hash matching the server-side crypto utility.
 * Computes: SHA-256(salt_bytes || plaintext_bytes) → hex string
 */
async function hashTokenClient(plaintext: string, saltBase64: string): Promise<string> {
  const saltBytes = Uint8Array.from(atob(saltBase64), (c) => c.charCodeAt(0));
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const combined = new Uint8Array(saltBytes.length + plaintextBytes.length);
  combined.set(saltBytes);
  combined.set(plaintextBytes, saltBytes.length);
  const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Direct client-side share token validation via Firestore.
 * Replaces the Cloud Function approach to eliminate cold-start delays.
 * Requires Firestore rules to allow unauthenticated `get` on shares/{linkId}.
 */
export async function validateShareToken(token: string, linkId?: string): Promise<ShareTokenValidationResult> {
  if (!token || !linkId) {
    return { valid: false, reason: 'Token and link ID are required' };
  }

  try {
    const linkRef = doc(db, 'shares', linkId);
    const linkSnap = await getDoc(linkRef);

    if (!linkSnap.exists()) {
      await logEvent('share_token_validate', { valid: false, reason: 'not_found' });
      return { valid: false, reason: 'Invalid or expired token' };
    }

    const data = linkSnap.data();

    // Check if disabled
    if (data.isDisabled) {
      await logEvent('share_token_validate', { valid: false, reason: 'disabled' });
      return { valid: false, reason: 'Link has been disabled' };
    }

    // Check expiry — handle Firestore Timestamp and ISO string
    if (data.expiresAt) {
      const expiryDate =
        data.expiresAt && typeof data.expiresAt.toDate === 'function'
          ? data.expiresAt.toDate()
          : new Date(data.expiresAt);
      if (!isNaN(expiryDate.getTime()) && expiryDate < new Date()) {
        await logEvent('share_token_validate', { valid: false, reason: 'expired' });
        return { valid: false, reason: 'Link has expired' };
      }
    }

    // Validate token hash
    if (!data.salt || !data.tokenHash) {
      return { valid: false, reason: 'Invalid share configuration' };
    }

    const computedHash = await hashTokenClient(token, data.salt);
    if (computedHash !== data.tokenHash) {
      await logEvent('share_token_validate', { valid: false, reason: 'hash_mismatch' });
      return { valid: false, reason: 'Invalid token' };
    }

    // Build expiry string for response
    const expiresAtStr: string | undefined =
      data.expiresAt && typeof data.expiresAt.toDate === 'function'
        ? data.expiresAt.toDate().toISOString()
        : data.expiresAt;

    const result: ShareTokenValidationResult = {
      valid: true,
      caseId: data.caseId,
      // 'role' is how createShareToken stores the access level ('evaluator')
      scope: data.role ?? data.scope ?? 'evaluator',
      expiresAt: expiresAtStr,
    };

    await logEvent('share_token_validate', { valid: true, reason: 'success', caseId: data.caseId });
    return result;
  } catch (err) {
    console.error('[validateShareToken] Error:', err);
    await logEvent('share_token_validate', { valid: false, reason: 'validation_error' });
    return { valid: false, reason: 'Validation error' };
  }
}

/**
 * Disable a share link by ID.
 * Admin action to revoke access.
 */
export async function disableShareLinkById(linkId: string): Promise<void> {
  const linkRef = doc(db, 'shares', linkId);
  await updateDoc(linkRef, {
    isDisabled: true,
  });
}

/**
 * Add watermark to PDF client-side (fallback).
 * For production, prefer server-side watermarking during export generation.
 */
export async function addWatermarkClient(
  pdfBlob: Blob,
  text: string
): Promise<Blob> {
  // Dynamically import pdf-lib to reduce bundle size
  const { PDFDocument, rgb, degrees } = await import('pdf-lib');
  
  const pdfBytes = await pdfBlob.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();
  
  // Add diagonal watermark to each page
  for (const page of pages) {
    const { width, height } = page.getSize();
    page.drawText(text, {
      x: width / 4,
      y: height / 2,
      size: 48,
      color: rgb(0.7, 0.7, 0.7),
      opacity: 0.3,
      rotate: degrees(45),
    });
  }
  
  const watermarkedBytes = await pdfDoc.save();
  
  // Log analytics on successful watermark
  await logEvent('share_watermark_applied', {
    watermarkText: text,
    pageCount: pages.length,
    fileSize: watermarkedBytes.length,
  });
  
  // Create new Uint8Array to satisfy blob type requirements
  return new Blob([new Uint8Array(watermarkedBytes)], { type: 'application/pdf' });
}

// ========================================
// Share Invite Acceptance & Revocation (Cloud Function callables)
// ========================================

/**
 * Accept a share invite link as the currently authenticated user.
 * Creates a VIEW_ONLY membership on the case and returns the redirect path.
 *
 * Must be called after the user is signed in — the Cloud Function requires auth.
 */
export async function acceptShareInvite(
  linkId: string,
  token: string,
): Promise<{ caseId: string; redirectPath: string; alreadyMember: boolean }> {
  const callable = httpsCallable<
    { linkId: string; token: string },
    { caseId: string; redirectPath: string; alreadyMember: boolean }
  >(functions, 'acceptShareInvite');

  const result = await callable({ linkId, token });
  return result.data;
}

/**
 * Revoke the calling user's own VIEW_ONLY access that was granted via a share link.
 * Also disables the share link so it cannot be accepted again.
 *
 * Must be called after the user is signed in — the Cloud Function requires auth.
 */
export async function revokeShareAccess(
  linkId: string,
  token: string,
): Promise<void> {
  const callable = httpsCallable<
    { linkId: string; token: string },
    { success: boolean }
  >(functions, 'revokeShareAccess');

  await callable({ linkId, token });
}
